import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Divider
} from '@mui/material';
import {
  BsSearch,
  BsQuestionCircleFill,
  BsBookFill,
  BsLightningChargeFill,
  BsPeopleFill,
  BsFolder2Open,
  BsClockHistory,
  BsShieldCheck,
  BsChevronDown,
  BsChatDotsFill,
  BsEnvelopeFill,
  BsKanbanFill
} from 'react-icons/bs';

const guides = [
  {
    icon: <BsKanbanFill size={24} color="#3b82f6" />,
    bg: '#eff6ff',
    title: "Projects & Work Management",
    description: "Learn how to create projects, assign tasks to team members, set deadlines, and track milestones using Route Maps and Kanban queues.",
    tags: ["Projects", "Tasks", "Route Map", "Workload"]
  },
  {
    icon: <BsPeopleFill size={24} color="#10b981" />,
    bg: '#ecfdf5',
    title: "Clients & Business Entities",
    description: "Manage client organizations, business types, tax identifiers (GST/VAT), billing currencies, and contact directories efficiently.",
    tags: ["Clients", "Currencies", "Business Types", "CSV Import"]
  },
  {
    icon: <BsClockHistory size={24} color="#f59e0b" />,
    bg: '#fffbeb',
    title: "Attendance & Leave Tracking",
    description: "Understand daily punch-in/out logs, tolerance window rules, overtime calculations, half-day criteria, and leave approval workflows.",
    tags: ["Attendance", "Punch In/Out", "Leaves", "Overtime"]
  },
  {
    icon: <BsFolder2Open size={24} color="#8b5cf6" />,
    bg: '#f5f3ff',
    title: "AI Document Repository & Archives",
    description: "Upload, organize, and archive project files, image collections, and legal documents with version control and AI-powered document search.",
    tags: ["Documents", "Archives", "File Upload", "Hub"]
  },
  {
    icon: <BsShieldCheck size={24} color="#ec4899" />,
    bg: '#fdf2f8',
    title: "User Roles & Access Permissions",
    description: "Configure role-based access control (RBAC), manage employee hierarchies, IT manager privileges, and department appraisals.",
    tags: ["Security", "Roles", "Permissions", "Employees"]
  },
  {
    icon: <BsLightningChargeFill size={24} color="#06b6d4" />,
    bg: '#ecfeff',
    title: "Real-time Chat & Notifications",
    description: "Collaborate seamlessly with instant team messaging, broadcast alerts, task assignment notifications, and audit logs.",
    tags: ["Chat", "Messages", "Alerts", "Collaboration"]
  }
];

const faqs = [
  {
    question: "How do I create a new Client or import multiple clients via CSV?",
    answer: "Navigate to the 'Clients' module from the sidebar. You can click the '+ New Client' button to fill out the organization form manually, or select 'Import CSV' from the top actions bar to upload a bulk spreadsheet containing business names, tax IDs, and currencies."
  },
  {
    question: "How does the Attendance calculation work when a user has not punched out yet?",
    answer: "When a user punches in for the day, the Attendance Report will mark their status as 'Active' and dynamically calculate their ongoing working hours up to the current moment. Once they punch out, the final duration is recorded and evaluated against daily threshold rules (e.g., Full Day >= 8 hours, Overtime > 9 hours)."
  },
  {
    question: "What is the difference between a Project Work and a Service Task?",
    answer: "Project Works represent structured deliverables with defined milestones, route maps, and multi-employee assignments. Service Tasks are recurring or operational tickets (e.g., maintenance, IT support, client inquiries) handled through rapid service queues."
  },
  {
    question: "How can I customize working time tolerance rules and shift timings?",
    answer: "Users with IT Manager or Admin privileges can access 'Time Settings' under the Employees menu. Here, you can configure official start times, grace period tolerances (in minutes), and automated late mark thresholds for daily attendance."
  },
  {
    question: "Where are uploaded files and image archives stored?",
    answer: "All project attachments, client agreements, and media archives are managed centrally in the 'Document Repository'. You can filter documents by category, view revision history, and securely share links across teams."
  }
];

export default function HelpView() {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const filteredGuides = guides.filter(g => 
    g.title.toLowerCase().includes(search.toLowerCase()) ||
    g.description.toLowerCase().includes(search.toLowerCase()) ||
    g.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredFaqs = faqs.filter(f =>
    f.question.toLowerCase().includes(search.toLowerCase()) ||
    f.answer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Container maxWidth="xl" className="py-4">
      {/* Hero Banner Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: '24px',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          mb: 5,
          boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.25)'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 680 }}>
          <Chip
            label="PROJECT KNOWLEDGE BASE"
            size="small"
            sx={{
              bgcolor: 'rgba(59, 130, 246, 0.2)',
              color: '#60a5fa',
              fontWeight: 700,
              fontSize: '11px',
              letterSpacing: '0.08em',
              mb: 2,
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '28px', md: '36px' },
              fontFamily: 'Poppins, sans-serif',
              lineHeight: 1.2,
              mb: 1.5,
              background: 'linear-gradient(to right, #ffffff, #94a3b8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            How can we help you today?
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '15px', mb: 3.5, lineHeight: 1.6 }}>
            Explore comprehensive guides, operational workflows, and answers tailored to your Task & Management System project.
          </Typography>

          <TextField
            fullWidth
            placeholder="Search guides, modules, attendance rules, FAQs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BsSearch color="#64748b" size={18} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: '#ffffff',
                borderRadius: '16px',
                height: 54,
                fontSize: '15px',
                color: '#0f172a',
                fontFamily: 'Poppins, sans-serif',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: '2px solid #3b82f6' }
              }
            }}
          />
        </Box>

        {/* Background Decorative Glow */}
        <Box
          sx={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: 450,
            height: 450,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(0,0,0,0) 70%)',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      </Paper>

      {/* Module Guides Grid */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          color: '#0f172a',
          fontFamily: 'Poppins, sans-serif',
          mb: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.2
        }}
      >
        <BsBookFill color="#3b82f6" /> Core Project Modules & Guides
      </Typography>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        {filteredGuides.map((guide, idx) => (
          <Grid item xs={12} md={6} lg={4} key={idx}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 35px -10px rgba(15, 23, 42, 0.08)',
                  borderColor: '#cbd5e1'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '14px',
                    bgcolor: guide.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  {guide.icon}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#0f172a', lineHeight: 1.3 }}>
                  {guide.title}
                </Typography>
              </Box>

              <Typography sx={{ color: '#64748b', fontSize: '13.5px', lineHeight: 1.6, mb: 2.5, flexGrow: 1 }}>
                {guide.description}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                {guide.tags.map((tag, tIdx) => (
                  <Chip
                    key={tIdx}
                    label={tag}
                    size="small"
                    sx={{
                      bgcolor: '#f8fafc',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '11px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px'
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Frequently Asked Questions Section */}
      <Grid container spacing={4}>
        <Grid item xs={12} lg={8}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              color: '#0f172a',
              fontFamily: 'Poppins, sans-serif',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2
            }}
          >
            <BsQuestionCircleFill color="#8b5cf6" /> Frequently Asked Questions
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredFaqs.map((faq, idx) => (
              <Accordion
                key={idx}
                expanded={expanded === `panel${idx}`}
                onChange={handleChange(`panel${idx}`)}
                elevation={0}
                sx={{
                  borderRadius: '16px !important',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  transition: 'all 0.2s ease',
                  '&.Mui-expanded': {
                    borderColor: '#8b5cf6',
                    boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.08)'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<BsChevronDown color="#64748b" />}
                  sx={{ px: 3, py: 1 }}
                >
                  <Typography sx={{ fontWeight: 650, fontSize: '15px', color: '#0f172a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Typography sx={{ color: '#475569', fontSize: '14px', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Grid>

        {/* Support & Assistance Box */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              borderRadius: '20px',
              background: 'linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)',
              border: '1px solid #dbeafe',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: '#3b82f6',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 10px 20px -5px rgba(59, 130, 246, 0.4)'
              }}
            >
              <BsChatDotsFill size={24} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#0f172a', mb: 1 }}>
              Need Custom Assistance?
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '13.5px', lineHeight: 1.6, mb: 3 }}>
              If you have specific questions about project configurations, custom permissions, or API integrations, contact the system administrator.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={<BsEnvelopeFill />}
              onClick={() => {
                window.location.href = "mailto:support@plakalin.com?subject=TMS%20Project%20Support%20Request";
              }}
              sx={{
                bgcolor: '#2563eb',
                color: '#ffffff',
                fontWeight: 650,
                py: 1.4,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '14px',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              Contact IT Support
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
